import {createBrowserRouter, RouterProvider} from 'react-router-dom';

import {initClientInterface} from '@zzng/common/scheme/digitalcard';

import {COMPONENT_BY_TYPE} from '@/root/components/popups';
import RecommendedCardIntroError from '@/root/components/RecommendCardIntroError';
import {ROUTES} from '@/root/constants/routes';
import {ServerErrorCode} from '@/root/constants/serverErrorCode';
import {PopupType} from '@/root/hooks/usePopup';
import RootPage from '@/root/pages';
import CardGroupPage from '@/root/pages/detail/:groupCode';
import CardDetailHalfview from '@/root/pages/detail/:groupCode/:cardId';
import CardListHalfview from '@/root/pages/detail/:groupCode/cards';
import CardNotFound from '@/root/pages/detail/card-not-found';
import RedriectToCardGroupPageByCardId from '@/root/pages/detail/group/:cardId';
import HomePage from '@/root/pages/home';
import RecommendCardIntroPage from '@/root/pages/home/:recommendCode';
import HomeSettingPage from '@/root/pages/home/setting';

import GlobalErrorBoundary from '@/shared/components/GlobalErrorBoundary';
import InAppGuardOutlet from '@/shared/components/InAppGuardOutlet';
import {DicaronCommonErrorCode} from '@/shared/constants/dicaronServerErrorCode';
import ForeignerPage from '@/shared/pages/error/foreigner';
import InvalidDevicePage from '@/shared/pages/error/invalid-device';
import LoginErrorPage from '@/shared/pages/error/login';
import NetworkErrorPage from '@/shared/pages/error/network';
import ServerErrorPage from '@/shared/pages/error/server';
import UnderMaintenancePage from '@/shared/pages/error/under-maintenance';
import InvalidVersionPage from '@/shared/pages/invalid-version';
import NotFoundPage from '@/shared/pages/not-found';
import composeProviders, {ProviderComponent} from '@/shared/providers/composeProviders';
import PopupProvider from '@/shared/providers/PopupProvider';
import ReactQueryClientProvider from '@/shared/providers/ReactQueryClientProvider';
import ToastProvider from '@/shared/providers/ToastProvider';
import {removeScrollRestoration} from '@/shared/utils/sessionStorage';
import {initDarkMode} from '@/shared/utils/theme';

import CardGroupDeletePage from '@/apps/root/pages/detail/:groupCode/delete';
import HomeDeletePage from '@/apps/root/pages/home/delete';
import PapasBridgePage from '@/apps/root/pages/papas-bridge';

initDarkMode();
initClientInterface();
removeScrollRestoration();

const pathByErrorCode = {
  [ServerErrorCode.CARD_NOT_FOUND]: ROUTES.CARD_NOT_FOUND,
  [DicaronCommonErrorCode.LOGIN_REQUIRED]: ROUTES.LOGIN_ERROR
};

const providers: ProviderComponent[] = [
  (props) => <GlobalErrorBoundary pathByErrorCodeOverrides={pathByErrorCode}>{props.children}</GlobalErrorBoundary>,
  (props) => <PopupProvider<PopupType> components={COMPONENT_BY_TYPE}>{props.children}</PopupProvider>,
  (props) => <ToastProvider>{props.children}</ToastProvider>
];

const AppProviders = composeProviders(...providers);

const router = createBrowserRouter([
  {index: true, element: <RootPage />},
  {
    element: (
      <AppProviders>
        <InAppGuardOutlet />
      </AppProviders>
    ),
    children: [
      {
        path: ROUTES.HOME,
        element: <HomePage />,
        children: [
          {path: ROUTES.SETTING, element: <HomeSettingPage />},
          {path: ROUTES.HOME_DELETE, element: <HomeDeletePage />},
          {
            path: ROUTES.RECOMMEND_CODE,
            element: <RecommendCardIntroPage />,
            errorElement: <RecommendedCardIntroError />
          }
        ]
      },
      {
        path: ROUTES.CARD_GROUP,
        element: <CardGroupPage />,
        children: [
          {
            path: ROUTES.CARD_LIST,
            element: <CardListHalfview />
          },
          {
            path: ROUTES.CARD_DETAIL,
            element: <CardDetailHalfview />
          },
          {
            path: ROUTES.CARD_GROUP_DELETE,
            element: <CardGroupDeletePage />
          }
        ]
      },
      {
        path: ROUTES.CARD_GROUP_BY_CARD_ID,
        element: <RedriectToCardGroupPageByCardId />
      },
      {
        path: ROUTES.PAPAS_BRIDGE,
        element: <PapasBridgePage />
      }
    ]
  },
  {path: ROUTES.INVALID_VERSION, element: <InvalidVersionPage />},
  {path: ROUTES.INVALID_DEVICE, element: <InvalidDevicePage />},
  {path: ROUTES.LOGIN_ERROR, element: <LoginErrorPage showLoginButton={false} />},
  {path: ROUTES.UNDER_MAINTENANCE, element: <UnderMaintenancePage />},
  {path: ROUTES.SERVER_ERROR, element: <ServerErrorPage />},
  {path: ROUTES.FOREIGNER, element: <ForeignerPage />},
  {path: ROUTES.NETWORK_ERROR, element: <NetworkErrorPage />},
  {path: ROUTES.CARD_NOT_FOUND, element: <CardNotFound />},
  {path: '*', element: <NotFoundPage />}
]);

const App = () => {
  return (
    <ReactQueryClientProvider>
      <RouterProvider router={router} />
    </ReactQueryClientProvider>
  );
};

export default App;
